import type { AlertProps, SelectProps, TextAreaProps, TextFieldProps, ToggleProps } from '@warp-ds/react';
import type { RegisterOptions } from 'react-hook-form';
import type { WidgetTypes } from './widget-types.js';

// These types should only cover what is expected in the models - not what the widget components themselves will accept (this should be extended in the component).

export type Condition = FieldCondition | ContextCondition;
type ConditionOperator = 'eq' | 'ne' | 'gt' | 'lt' | 'gte' | 'lte' | 'in';
type ConditionValue = string | number | Array<string | number | boolean> | 'current-year';
export type ValueCondition = {
  operator: ConditionOperator;
  value: ConditionValue;
};
// Value does not make sense when we just check for presence or absence
type SimpleCondition = {
  operator: 'exists' | 'absent' | 'falsyOrAbsent';
};
type BaseCondition = ValueCondition | SimpleCondition;

type FieldCondition = {
  field: string;
} & BaseCondition;

type ContextCondition = {
  context: 'brand' | 'mode' | 'locale' | 'orgId';
} & BaseCondition;

type StandardWidgetModel = {
  name: string;
  label?: string;
  mandatory?: boolean;
  readonly?: boolean;
  className?: string;
  helpText?: string;
  conditions?: Condition[];
  defaultValue?: string | number;
  /** Unleash toggle name. Widget is only included if this toggle is enabled. */
  featureToggle?: string;
};

type InputWidgetModel = {
  rules?: Pick<RegisterOptions, 'max' | 'min' | 'maxLength' | 'minLength' | 'required'>;
};

export type ValueOption = { id?: number | string; code: string; label: string };

export type HeaderWidgetModel = {
  type: typeof WidgetTypes.HEADER;
  headerType?: 'title' | 'sub' | 'step-title' | 'section-title';
  text: string;
} & StandardWidgetModel;

export const InfoModalType = {
  REGTAX_INFO: 'regtax_info',
} as const;

export type AlertWidgetModel = {
  type: typeof WidgetTypes.ALERT;
  alertType: AlertProps['type'];
  text: string;
  title?: string;
  links?: { text: string; url: string }[];
  infoModal?: {
    type: (typeof InfoModalType)[keyof typeof InfoModalType];
    text: string;
  };
} & StandardWidgetModel;

// Making a union with the Warp component props allows us to overwrite these props in the models
export type TextFieldWidgetModel = {
  type:
    | typeof WidgetTypes.STRING
    | typeof WidgetTypes.NUMBER
    | typeof WidgetTypes.DECIMAL
    | typeof WidgetTypes.PHONE
    | typeof WidgetTypes.POSTALCODE;
  formatAsNumber?: boolean;
  saveAsString?: boolean;
  suffix?: string;
  prefix?: string;
} & Partial<Omit<TextFieldProps, 'type'>> &
  StandardWidgetModel &
  InputWidgetModel;

export type TextAreaWidgetModel = {
  type: typeof WidgetTypes.TEXTAREA;
} & Partial<Omit<TextAreaProps, 'type'>> &
  StandardWidgetModel &
  InputWidgetModel;

export type ToggleWidgetModel = {
  type: typeof WidgetTypes.RADIOGROUP | typeof WidgetTypes.CHECKBOXGROUP;
} & Partial<Omit<ToggleProps, 'type'>> &
  StandardWidgetModel &
  InputWidgetModel;

type HiddenStringWidgetModel = {
  type: typeof WidgetTypes.HIDDENSTRING;
} & StandardWidgetModel;

type GroupWidgetModel = {
  type: typeof WidgetTypes.GROUP;
  children: WidgetModel[];
} & StandardWidgetModel;

export type SelectWidgetModel = {
  type: typeof WidgetTypes.SELECT;
} & Partial<Omit<SelectProps, 'type'>> &
  StandardWidgetModel;

export type ComboboxWidgetModel = {
  type: typeof WidgetTypes.COMBOBOX;
} & StandardWidgetModel;

export type DateWidgetModel = {
  type: typeof WidgetTypes.DATE;
} & StandardWidgetModel;

export type BooleanWidgetModel = {
  type: typeof WidgetTypes.BOOLEAN;
} & StandardWidgetModel &
  InputWidgetModel;

export type RadioButtonWidgetModel = {
  type: typeof WidgetTypes.RADIOBUTTON;
  asBoolean?: boolean;
} & StandardWidgetModel &
  InputWidgetModel;

export type ImageWidgetModel = {
  type: typeof WidgetTypes.IMAGE;
} & StandardWidgetModel &
  InputWidgetModel;

export type HtmlWidgetModel = {
  type: typeof WidgetTypes.HTML;
} & StandardWidgetModel &
  InputWidgetModel;

export type PriceWidgetModel = {
  type: typeof WidgetTypes.PRICE;
  suffix?: string;
  formatAsNumber?: boolean;
} & StandardWidgetModel &
  InputWidgetModel;

type UsedCarGuaranteeWidgetModel = {
  type: typeof WidgetTypes.USEDCARGUARANTEE;
} & StandardWidgetModel;

export type ConditionWidgetModel = {
  type: typeof WidgetTypes.CONDITION;
} & StandardWidgetModel;

export type ModelAndMakeWidgetModel = {
  type: typeof WidgetTypes.MODELANDMAKE;
} & StandardWidgetModel;

export type EquipmentWidgetModel = {
  type: typeof WidgetTypes.EQUIPMENT;
  headerType?: 'sub' | 'section-title';
} & StandardWidgetModel;

export type ProfileWidgetModel = {
  type: typeof WidgetTypes.PROFILE;
} & StandardWidgetModel;

type CountryWidgetModel = {
  type: typeof WidgetTypes.COUNTRY;
} & StandardWidgetModel;

export type LeasingTerminationWidgetModel = {
  type: typeof WidgetTypes.LEASINGTERMINATION;
} & StandardWidgetModel;

export type StreetWidgetModel = {
  type: typeof WidgetTypes.STREET;
} & StandardWidgetModel &
  InputWidgetModel;

type PostalcodeWidgetModel = {
  type: typeof WidgetTypes.POSTALCODE;
} & StandardWidgetModel &
  InputWidgetModel;

export type YesNoWidgetModel = {
  type: typeof WidgetTypes.YESNO;
} & StandardWidgetModel;

export type WidgetModel =
  | HeaderWidgetModel
  | AlertWidgetModel
  | TextFieldWidgetModel
  | ToggleWidgetModel
  | HiddenStringWidgetModel
  | GroupWidgetModel
  | SelectWidgetModel
  | ComboboxWidgetModel
  | DateWidgetModel
  | BooleanWidgetModel
  | ImageWidgetModel
  | HtmlWidgetModel
  | PriceWidgetModel
  | UsedCarGuaranteeWidgetModel
  | ModelAndMakeWidgetModel
  | EquipmentWidgetModel
  | ConditionWidgetModel
  | ProfileWidgetModel
  | CountryWidgetModel
  | LeasingTerminationWidgetModel
  | TextAreaWidgetModel
  | StreetWidgetModel
  | PostalcodeWidgetModel
  | YesNoWidgetModel
  | RadioButtonWidgetModel;

export type WidgetModelArray = Array<WidgetModel>;
export type WizardWidgetModelArray = Array<WidgetModelArray>;
